import { __rest, __assign } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import assert from 'assert';
import { forwardRef, useRef, useEffect, cloneElement } from 'react';
import { Box } from '../Box/index.js';
import { Inline } from '../Inline/Inline.js';
import '../hooks/useDetectInputMethod.js';
import '@reach/auto-id';
import { useTheme } from '../hooks/useTheme.js';
import { getTabId, getPanelId } from './utils/generateIds.js';
import { useTabContext } from './contexts/TabContext.js';
import { useTabsContext } from './contexts/TabsContext.js';
import { useTabListContext } from './contexts/TabsListContext.js';
import { smoothScrollIntoView } from '../utils/smoothScroll.js';

var Tab = forwardRef(function (_a, ref) {
    var children = _a.children, pill = _a.pill, icon = _a.icon, props = __rest(_a, ["children", "pill", "icon"]);
    var _b = useTabsContext(), id = _b.id, size = _b.size, selectedId = _b.selectedId, setSelectedId = _b.setSelectedId, onChange = _b.onChange;
    var currentTabIndex = useTabContext().currentTabIndex;
    var _c = useTabListContext(), tabsRefs = _c.tabsRefs, tabListContainer = _c.tabListContainer;
    var tabsList = tabsRefs.current;
    assert(
    // @ts-expect-error "Accessing a custom made property"
    !pill || pill.type.__isPill__, "You can only use Pills in the pill spot");
    assert(!pill || pill.props.size === undefined, "Elements in pill cannot set the size prop when passed to a Tab component");
    assert(!icon || (icon.props.size === undefined && icon.props.tone === undefined), "Icons cannot set the 'size' or 'tone' prop when passed to a Tab component");
    var isSelected = selectedId === currentTabIndex;
    var tabIndex = isSelected ? 0 : -1;
    var handleClick = function (event) {
        event.stopPropagation();
        event.preventDefault();
        onChange === null || onChange === void 0 ? void 0 : onChange({ index: currentTabIndex, title: children });
        setSelectedId(currentTabIndex);
    };
    var onKeyUp = function (event) {
        var targetKey = event.key;
        if (targetKey === "Tab") {
            return;
        }
    };
    var triggerElement = function (element) {
        element.click();
        element.focus();
    };
    var onKeyDown = function (event) {
        var targetKey = event.key;
        // Prevent arrow keys scrolling the document while navigating the tabs
        var isArrowPress = targetKey.indexOf("Arrow") === 0;
        // Prevent enter or space press from triggering the click handler
        var isActionKeyPress = targetKey === "Enter" || targetKey === " ";
        if (isArrowPress || isActionKeyPress) {
            event.preventDefault();
        }
        var firstTabIndex = 0;
        var lastTabIndex = tabsList.length - 1;
        var nextTabIndex = currentTabIndex < lastTabIndex ? currentTabIndex + 1 : firstTabIndex;
        var prevTabIndex = currentTabIndex > firstTabIndex ? currentTabIndex - 1 : lastTabIndex;
        if (targetKey === "ArrowLeft") {
            triggerElement(tabsList[prevTabIndex]);
        }
        if (targetKey === "ArrowRight") {
            triggerElement(tabsList[nextTabIndex]);
        }
        if (targetKey === "End") {
            triggerElement(tabsList[lastTabIndex]);
        }
        if (targetKey === "Home") {
            triggerElement(tabsList[firstTabIndex]);
        }
    };
    var firstRenderRef = useRef(true);
    useEffect(function () {
        if (!tabsList[currentTabIndex] || !tabListContainer) {
            return;
        }
        if (isSelected) {
            smoothScrollIntoView(tabsList[currentTabIndex], {
                scrollContainer: tabListContainer,
                direction: "horizontal",
                offset: 10 * 6 * 6,
                speed: 0.7,
            });
        }
        firstRenderRef.current = false;
    }, [isSelected, tabListContainer, currentTabIndex, tabsList]);
    var handleStyleProps = useTheme().handleStyleProps;
    var css = handleStyleProps({
        size: size,
        themeKey: "tabLink",
    }).css;
    return (jsx(Box, __assign({ id: getTabId(id, currentTabIndex), themeKey: "tabLink", sx: css, component: "button", type: "button", ref: ref, role: "tab", "aria-selected": isSelected, "aria-controls": getPanelId(id, currentTabIndex), tabIndex: tabIndex, onClick: handleClick, onKeyUp: onKeyUp, onKeyDown: onKeyDown }, props, { children: jsxs(Inline, __assign({ gap: "3", noWrap: true }, { children: [icon, children, pill ? cloneElement(pill, { size: size }) : null] })) })));
});
Tab.displayName = "Tab";

export { Tab };
//# sourceMappingURL=Tab.js.map
